import axios from 'axios';
import { cachedApiCall, cacheKeys, invalidateCache } from '../lib/cache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Cache TTL: 15 minutes for settings (they don't change frequently)
const SETTINGS_CACHE_TTL = 15 * 60 * 1000;

// AI Persona Settings (Admin only)
export const fetchAIPersonaSettings = async (token, options = {}) => {
  return cachedApiCall(
    cacheKeys.settingsAIPersona(),
    async () => {
      const response = await axios.get(`${API_BASE_URL}/api/ai-voice-caller/ai-persona-settings/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    SETTINGS_CACHE_TTL,
    options
  );
};

export const updateAIPersonaSettings = async (token, data) => {
  const response = await axios.post(`${API_BASE_URL}/api/ai-voice-caller/ai-persona-settings/`, data, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after update
  invalidateCache.settingsAIPersona();
  return response.data;
};

// Chatbot Settings (Admin only)
export const fetchChatbotSettings = async (token, options = {}) => {
  return cachedApiCall(
    cacheKeys.settingsChatbot(),
    async () => {
      const response = await axios.get(`${API_BASE_URL}/api/chatbot/settings/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    SETTINGS_CACHE_TTL,
    options
  );
};

export const updateChatbotSettings = async (token, data) => {
  const response = await axios.post(`${API_BASE_URL}/api/chatbot/settings/`, data, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after update
  invalidateCache.settingsChatbot();
  return response.data;
};

// Voice Assistant Settings (Admin only)
export const fetchVoiceAssistants = async (token, options = {}) => {
  return cachedApiCall(
    cacheKeys.settingsVoiceAssistants(),
    async () => {
      const response = await axios.get(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    SETTINGS_CACHE_TTL,
    options
  );
};

export const fetchVoiceAssistant = async (token, id) => {
  const response = await axios.get(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/${id}/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const createVoiceAssistant = async (token, data) => {
  const response = await axios.post(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/`, data, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after create
  invalidateCache.settingsVoiceAssistants();
  return response.data;
};

export const updateVoiceAssistant = async (token, id, data) => {
  const response = await axios.patch(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/${id}/`, data, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after update
  invalidateCache.settingsVoiceAssistants();
  return response.data;
};

export const deleteVoiceAssistant = async (token, id) => {
  const response = await axios.delete(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/${id}/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after delete
  invalidateCache.settingsVoiceAssistants();
  return response.data;
};

export const syncVoiceAssistant = async (token, id) => {
  const response = await axios.post(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/${id}/sync/`, {}, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after sync
  invalidateCache.settingsVoiceAssistants();
  return response.data;
};

export const syncAllVoiceAssistants = async (token) => {
  const response = await axios.post(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/sync_all/`, {}, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Invalidate cache after sync
  invalidateCache.settingsVoiceAssistants();
  return response.data;
};

export const testVapiConnection = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/test_connection/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const getVoiceOptions = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/api/ai-voice-caller/voice-assistants/voice_options/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

